<template>
  <div id="map-section" class="home-map-section" v-if="!isLoading">
    <ShipSection
      :ships="assets"
      @toggle-trail="handleTrailToggle"
      @trail-range="handleTrailRange"
      @zoom-to-ship="zoomToShip"
      @toggle-visibility="handleVisibilityToggle"
      @show-all-ships="handleShowAll"
      @hide-all-ships="handleHideAll"
      @set-base-layer="handleSetBaseLayer"
      @set-icon-style="handleSetIconStyle"
      @set-icon-scale="handleSetIconScale"
      @toggle-landmarks="handleToggleLandmarks"
      @toggle-timezones="handleToggleTimezones"
      @ship-expanded="handleShipExpanded"
      @hover-ship="handleHoverShip"
    />
    <div class="home-map-wrapper">
      <Map
        ref="mapView"
        :ships="assets"
        SelectedMap="default"
        :hideSettingsControl="true"
        @map-mode-change="handleMapModeChange"
      />
    </div>
  </div>
  <div class="text-center text-danger my-2" v-else>
    <wave-loader size="lg">Loading...</wave-loader>
  </div>
</template>

<script>
import Map from '@/views/map';
import ShipSection from '@/views/home/components/ShipSection';
import { mapState } from 'vuex';

const REFRESH_MS = 60 * 1000;
// Trail ga usah ikut tiap menit: kapal 10 knot cuma jalan ~300 m, sub-piksel di
// zoom fleet. 10 menit (~3 km) baru keliatan manjang.
const TRAIL_EVERY = 10;

export default {
  name: 'Home',
  components: {
    Map,
    ShipSection,
  },
  data() {
    return {
      isLoading: true,
      mapMode: 'standard',
    };
  },
  computed: {
    ...mapState('asset', {
      assets: (state) => state.assets.filter((asset) => asset.lastGpsData),
    }),
  },
  methods: {
    async fetchData() {
      await this.$store.dispatch('asset/fetchAndReconcileAssets');
    },
    handleMapModeChange(mode) {
      // Fetch new data based on mode when switching
      this.mapMode = mode;
      this.$store.dispatch('asset/getLastData', mode);
    },
    async tick() {
      if (document.hidden || this._busy) return;
      // Rebuild peta nutup popup yang lagi kebuka — bug lama, kepicu tiap
      // SET_ASSETS. Tunda refresh selama user masih baca popup kapal.
      if (document.querySelector('.leaflet-popup')) return;
      this._busy = true;
      try {
        this._ticks = (this._ticks || 0) + 1;
        if (this._ticks % TRAIL_EVERY === 0
          && this.$refs.mapView && this.$refs.mapView.mapService) {
          this.$refs.mapView.mapService.expireTrails();
        }
        await this.$store.dispatch('asset/getLastData', this.mapMode);
      } catch (err) {
        console.error('[home autorefresh]', err);
      } finally {
        this._busy = false;
      }
    },
    zoomToShip(ship) {
      // eslint-disable-next-line no-undef
      if (this.$refs.mapView && this.$refs.mapView.mapService && ship.lastLatLng) {
        this.$refs.mapView.mapService.panToShip(ship);
      }
    },
    async handleTrailToggle(payload) {
      const ship = this.assets.find((s) => s.massId === payload.massId);
      if (!ship) return;

      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        // Pas nyalain, paksa refetch biar window-nya ngikut chip yg lagi dipilih (ga pake cache lama).
        await this.$refs.mapView.mapService.toggleTrail(
          payload.trail, ship, payload.days || 1, payload.trail === true,
        );
      }
    },
    async handleTrailRange(payload) {
      const ship = this.assets.find((s) => s.massId === payload.massId);
      if (!ship) return;

      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        const svc = this.$refs.mapView.mapService;
        // Refetch + gambar ulang dgn range baru. toggleTrail udah buang polyline lama tepat sebelum
        // gambar yg baru, jadi kalo window baru kosong trail lama tetep keliatan (ga ilang gitu aja).
        await svc.toggleTrail(true, ship, payload.days || 1, true);
      }
    },
    handleVisibilityToggle(payload) {
      this.$store.dispatch('asset/updateAssetVisibility', payload);
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.toggleShipVisibility(payload.massId, payload.isVisible);
      }
    },
    handleShowAll() {
      this.$store.dispatch('asset/setAllAssetsVisibility', true);
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.showAllShips();
      }
    },
    handleHideAll() {
      this.$store.dispatch('asset/setAllAssetsVisibility', false);
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.hideAllShips();
      }
    },
    handleSetBaseLayer(key) {
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.setBaseLayer(key);
      }
    },
    handleSetIconStyle(style) {
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.setIconStyle(style);
      }
    },
    handleSetIconScale(scale) {
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.setIconScale(scale);
      }
    },
    handleToggleLandmarks(visible) {
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.toggleLandmarks(visible);
      }
    },
    handleToggleTimezones(visible) {
      if (this.$refs.mapView && this.$refs.mapView.mapService) {
        this.$refs.mapView.mapService.toggleTimezones(visible);
      }
    },
    handleShipExpanded({ massId }) {
      const ship = this.assets.find((s) => s.massId === massId);
      if (ship && this.$refs.mapView) this.$refs.mapView.ensureShipSummary(ship);
    },
    handleHoverShip(massId) {
      const svc = this.$refs.mapView && this.$refs.mapView.mapService;
      if (svc) svc.highlightShip(massId);
    },
    // Tautan dalam cuma di Home.
    readViewFromUrl() {
      const q = new URLSearchParams(window.location.search);
      const readNum = (key) => {
        const value = q.get(key);
        return value === null || value === '' ? NaN : Number(value);
      };
      const lat = readNum('lat');
      const lng = readNum('lng');
      const zoom = readNum('z');
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
      return { lat, lng, zoom };
    },
    // Pakai replaceState, bukan router.replace.
    writeViewToUrl(lat, lng, zoom) {
      if (this._urlTimer) clearTimeout(this._urlTimer);
      this._urlTimer = setTimeout(() => {
        const rounded = (v) => Number(v).toFixed(5);
        window.history.replaceState(null, '', `?lat=${rounded(lat)}&lng=${rounded(lng)}&z=${zoom}`);
      }, 500);
    },
    applyDeepLink() {
      const svc = this.$refs.mapView && this.$refs.mapView.mapService;
      if (!svc) return;
      svc.onMapMoved = this.writeViewToUrl;

      const massId = new URLSearchParams(window.location.search).get('ship');
      if (massId) svc.pendingPopupMassId = massId;
    },
  },
  async mounted() {
    try {
      await this.fetchData();
    } finally {
      this.isLoading = false;
    }
    this._initialView = this.readViewFromUrl();
    await this.$nextTick();
    const svc = this.$refs.mapView && this.$refs.mapView.mapService;
    if (svc && this._initialView) svc.initialView = this._initialView;
    this.applyDeepLink();
    this._poll = setInterval(this.tick, REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this._poll);
    if (this._urlTimer) clearTimeout(this._urlTimer);
  },
};
</script>

<style scoped>
.home-map-section {
  position: relative;
  display: flex;
  height: calc(100vh - 70px);
  width: 100%;
  overflow: hidden;
}

.home-map-wrapper {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
}
</style>
